import React = require("react");
import { H2 } from "../../../designSystem/typography/Heading";
import { getSurveyTemplates, loadSurveyTemplate, updateSurveyIntroduction } from "../../../api/surveyClientApi";
import { SegmentationPanel } from "../../surveys/editor/SegmentationPanel";
import { SurveyInfoPanel } from "../../surveys/editor/SurveyInfoPanel";
import { SurveyStatusPanel } from "../../surveys/editor/SurveyStatusPanel";
import { ThemeList } from "../../surveys/editor/ThemeList";
import { WorkplaceAssessmentComponentProps } from './WorkplaceAssessmentComponentProps';
import { LoadingPageOverlay } from "../../../designSystem/LoadingPageOverlay";
import { LanguageCode, SurveyStatus, SurveyTemplateView, fieldValidation, getSurveyStatus } from '../../../amorg-common';
import { SurveyTemplatesModal } from "../../surveys/editor/SurveyTemplatesModal";
import { TextAreaSaveField } from "../../../designSystem/formField/textArea/TextAreaSaveField";
import { ToolBarButton, ToolBarContainer } from "../../../designSystem/compositeComponents/ButtonToolBar";
import { IndicatorPanel } from "../../surveys/editor/IndicatorPanel";
import { PageHeader } from "../../../designSystem/PageHeader";
import { Card } from "../../../designSystem/Card";
import { PageSection } from "../../../designSystem/PageSection";

export function SurveyPanel(props: WorkplaceAssessmentComponentProps): JSX.Element 
{
    const [templates, setTemplates] = React.useState<SurveyTemplateView[]>([]);
    const [showTemplatesModal, setShowTemplatesModal] = React.useState(false);
    const [selectedLanguage, setSelectedLanguage] = React.useState<LanguageCode>(props.survey.languageCode);
    
    React.useEffect(() => {
        const getData = async () => {
            setTemplates(await getSurveyTemplates());
        };
        getData();
    }, [props.survey]);

    async function handleSelectTemplate(templateId: string) {
        await loadSurveyTemplate(props.survey.id, templateId);
        setShowTemplatesModal(false);
        props.onSurveyUpdated();
    }

    async function handleSaveIndtroduction(introduction:string) {
        await updateSurveyIntroduction(
            props.survey.id,
            introduction,
            selectedLanguage
        );
        props.onSurveyUpdated();
    }

    let surveyStatus:SurveyStatus|undefined = undefined;

    let disableEdit = true;
    if (props.survey){
        surveyStatus = getSurveyStatus(props.survey);
        disableEdit = !surveyStatus.canEdit;
    }

    const introText = selectedLanguage === props.survey.languageCode
        ? props.survey.introduction.text
        : props.survey.introduction.translations.find(t => t.languageCode === selectedLanguage).text;

    return (

        <>
        <LoadingPageOverlay loading={props.surveyLoading} />

        <SurveyTemplatesModal
            show={showTemplatesModal}
            templates={templates} 
            currentSurvey={props.survey}
            onTemplatesChanged={async ()=>setTemplates(await getSurveyTemplates())}
            onSelectTemplate={handleSelectTemplate}
            disabled={disableEdit} 
            onClose={()=>setShowTemplatesModal(false)}
        />

        {props.survey &&
            <>
            <PageHeader
                headerText="Spørgeskema"
                headerStyle="secondary-gray"
                buttons={
                    <ToolBarContainer>
                        <ToolBarButton
                            icon="interface_actions_save"
                            tooltip="Vis skabeloner"
                            onClick={ disableEdit ? undefined : ()=>setShowTemplatesModal(true)}
                            variant="text" 
                            disabled={disableEdit}
                            />
                    </ToolBarContainer>
                }
            />

            <SurveyStatusPanel survey={props.survey} />                    

            <Card variant="outlined">
                
                <SurveyInfoPanel 
                    activity={props.activity} survey={props.survey} 
                    onSurveyUpdated={props.onSurveyUpdated}
                    onActivityUpdated={props.onActivityUpdated}
                    selectedLanguageCode={selectedLanguage}
                    disableEdit={disableEdit}
                    onSelectedLanguageCodeChanged={(code)=>setSelectedLanguage(code)} />

                <PageSection
                    header={<H2 color="gray">Introduktion til respondenten</H2>}
                    defaultCollapsed={false}>
                        <TextAreaSaveField
                            label=''
                            minRows={3}
                            value={introText}
                            onSave={(val) => {
                                if(fieldValidation.survey.introductionText(val))
                                    handleSaveIndtroduction(val);
                            }}
                            validator={(currentValue)=>{
                                return fieldValidation.survey.introductionText(currentValue)
                                    ? {valid:true}
                                    : {valid:false, errorMessage:`Ugyldig titel. Skal udfyldes med maks. 1000 (${currentValue.length}) tegn.`};
                            }}
                            disabled={disableEdit} />
                </PageSection>
            </Card>
                
                    
            <IndicatorPanel survey={props.survey} />

            <PageSection
                header={
                    <H2 color="gray" >Segmenteringer</H2>
                }
                defaultCollapsed={false}>
                    <SegmentationPanel
                        selectedLanguage={selectedLanguage}
                        survey={props.survey}
                        disableEdit={disableEdit}
                        onUpdated={props.onSurveyUpdated} />
                
            </PageSection>
                
            <div>
                <ThemeList
                    languageCode={selectedLanguage}
                    survey={props.survey}
                    onSurveyUpdated={props.onSurveyUpdated}
                />
            </div>
            </>
        }
        </>     
    );
}